import React, { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'sidebar_desktop_collapsed';

interface SidebarContextValue {
    isMobileOpen: boolean;
    openMobile: () => void;
    closeMobile: () => void;
    toggleMobile: () => void;
    isDesktopCollapsed: boolean;
    toggleDesktop: () => void;
    toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isDesktopCollapsed, setIsDesktopCollapsed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, String(isDesktopCollapsed));
        } catch {}
    }, [isDesktopCollapsed]);

    const toggleDesktop = () => setIsDesktopCollapsed(prev => !prev);

    const toggleSidebar = () => {
        if (typeof window !== 'undefined' && window.innerWidth <= 900) {
            setIsMobileOpen(prev => !prev);
        } else {
            toggleDesktop();
        }
    };

    const value: SidebarContextValue = {
        isMobileOpen,
        openMobile: () => setIsMobileOpen(true),
        closeMobile: () => setIsMobileOpen(false),
        toggleMobile: () => setIsMobileOpen(prev => !prev),
        isDesktopCollapsed,
        toggleDesktop,
        toggleSidebar
    };

    return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = (): SidebarContextValue => {
    const ctx = useContext(SidebarContext);
    if (!ctx) {
        return {
            isMobileOpen: false,
            openMobile: () => {},
            closeMobile: () => {},
            toggleMobile: () => {},
            isDesktopCollapsed: false,
            toggleDesktop: () => {},
            toggleSidebar: () => {}
        };
    }
    return ctx;
};